import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import TextForm from '../TextForm/TextForm';
import PrivateTerms from '../Terms/PrivateTerms';
import Modal from '../Modal/Modal';
import EvaluacionDetalladaService from '../../services/evaluacionDetalladaService';
import ReclutamientoService from '../../services/reclutamientoService';
import './PrivateApplicationForm.css';

const PrivateApplicationForm = ({ onSubmit, isMobile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [reclutamientoId, setReclutamientoId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Verificar el token y obtener el ID del reclutamiento
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        console.error('No se proporcionó un token');
        setSubmitError(t('form.error.noToken'));
        setIsLoading(false);
        return;
      }

      try {
        const response = await ReclutamientoService.validateToken(token);
        if (response && response.reclutamientoId) {
          setReclutamientoId(response.reclutamientoId);
        } else {
          setSubmitError(t('form.error.invalidToken'));
        }
      } catch (error) {
        console.error('Error al validar el token:', error);
        setSubmitError(t('form.error.invalidToken'));
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token, t]);

  const [formData, setFormData] = useState({
    availability: '',
    motivation: '',
    learning: '',
    preguntaAbierta1: '',
    preguntaAbierta2: '',
    preguntaAbierta3: '',
    preguntaAbierta4: '',
    preguntaAbierta5: '',
    preguntaAbierta6: '',
    preguntaAbierta7: '',
    preguntaAbierta8: '',
    preguntaAbierta9: '',
    preguntaAbierta10: '',
    terms: false
  });

  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    if (showTerms) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }
  }, [showTerms]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Comprobamos que todos los campos requeridos estén completos
      if (!formData.availability || formData.availability.trim() === '') {
        throw new Error('La disponibilidad es requerida');
      }
      if (!formData.motivation || formData.motivation.trim() === '') {
        throw new Error('La motivación es requerida');
      }
      if (!formData.learning || formData.learning.trim() === '') {
        throw new Error('El objetivo de aprendizaje es requerido');
      }
      if (!formData.preguntaAbierta1 || formData.preguntaAbierta1.trim() === '') {
        throw new Error('La pregunta abierta 1 es requerida');
      }
      
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      if (!siteKey) {
        console.error('REACT_APP_RECAPTCHA_SITE_KEY no está definida');
      }
      if (!window.grecaptcha || typeof window.grecaptcha.execute !== 'function') {
        throw new Error('reCAPTCHA no se ha cargado. Intenta de nuevo en unos segundos.');
      }
      await new Promise((resolve) => window.grecaptcha.ready(resolve));
      const recaptchaToken = await window.grecaptcha.execute(siteKey, { action: 'submit' });
      
      // Nos aseguramos de que los nombres de los campos coincidan exactamente con el backend
      const evaluacionData = {
        reclutamientoId,
        token,
        pregunta1: formData.availability,
        pregunta2: formData.motivation,
        pregunta3: formData.learning,
        preguntaAbierta1: formData.preguntaAbierta1,
        preguntaAbierta2: formData.preguntaAbierta2,
        preguntaAbierta3: formData.preguntaAbierta3,
        preguntaAbierta4: formData.preguntaAbierta4,
        preguntaAbierta5: formData.preguntaAbierta5,
        preguntaAbierta6: formData.preguntaAbierta6,
        preguntaAbierta7: formData.preguntaAbierta7,
        preguntaAbierta8: formData.preguntaAbierta8,
        preguntaAbierta9: formData.preguntaAbierta9,
        preguntaAbierta10: formData.preguntaAbierta10,
        terms: formData.terms,
        recaptchaToken
      };

      console.log('Enviando evaluación:', evaluacionData);
      
      const response = await EvaluacionDetalladaService.createEvaluacionDetallada(evaluacionData);
      console.log('Respuesta del servidor:', response);
      
      // Invalidar el token después de un envío exitoso
      await ReclutamientoService.invalidateToken(token);
      
      // Redirigir a la página de éxito para formulario privado
      navigate('/private-submission-success');
      
    } catch (error) {
      console.error('Error al enviar evaluación detallada:', error);
      if (error.response) {
        console.error('Error del servidor:', error.response.data);
        setSubmitError(error.response.data.message || t('form.error.submit'));
      } else {
        setSubmitError(error.message || t('form.error.submit'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptTerms = () => {
    setFormData(prev => ({
      ...prev,
      terms: true
    }));
    setShowTerms(false);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-message">
          {t('form.loading')}
        </div>
      </div>
    );
  }

  if (submitError) {
    return (
      <div className="error-container">
        <div className="error-message">
          {submitError}
        </div>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit} className={`private-application-form ${isMobile ? 'mobile-form' : ''}`}>
        {/* Preguntas iniciales */}
        <TextForm
          label={t('privateForm.fields.availability.label')}
          name="availability"
          value={formData.availability}
          onChange={handleChange}
          placeholder={t('privateForm.fields.availability.placeholder')}
          required
          multiline
          className="full-width"
        />
        
        <TextForm
          label={t('privateForm.fields.motivation.label')}
          name="motivation"
          value={formData.motivation}
          onChange={handleChange}
          placeholder={t('privateForm.fields.motivation.placeholder')}
          required
          multiline
          className="full-width"
        />
        
        <TextForm
          label={t('privateForm.fields.learning.label')}
          name="learning"
          value={formData.learning}
          onChange={handleChange}
          placeholder={t('privateForm.fields.learning.placeholder')}
          required
          multiline
          className="full-width"
        />
        
        {/* 10 preguntas principales */}
        <TextForm
          label={t('privateForm.questions.q1')}
          name="preguntaAbierta1"
          value={formData.preguntaAbierta1}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        <TextForm
          label={t('privateForm.questions.q2')}
          name="preguntaAbierta2"
          value={formData.preguntaAbierta2}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        <TextForm
          label={t('privateForm.questions.q3')}
          name="preguntaAbierta3"
          value={formData.preguntaAbierta3}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        <TextForm
          label={t('privateForm.questions.q4')}
          name="preguntaAbierta4"
          value={formData.preguntaAbierta4}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        <TextForm
          label={t('privateForm.questions.q5')}
          name="preguntaAbierta5"
          value={formData.preguntaAbierta5}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        <TextForm
          label={t('privateForm.questions.q6')}
          name="preguntaAbierta6"
          value={formData.preguntaAbierta6}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        <TextForm
          label={t('privateForm.questions.q7')}
          name="preguntaAbierta7"
          value={formData.preguntaAbierta7}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        <TextForm
          label={t('privateForm.questions.q8')}
          name="preguntaAbierta8"
          value={formData.preguntaAbierta8}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        <TextForm
          label={t('privateForm.questions.q9')}
          name="preguntaAbierta9"
          value={formData.preguntaAbierta9}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        <TextForm
          label={t('privateForm.questions.q10')}
          name="preguntaAbierta10"
          value={formData.preguntaAbierta10}
          onChange={handleChange}
          required
          multiline
          className="full-width"
        />
        
        {/* Términos y condiciones */}
        <div className="terms">
          <input
            type="checkbox"
            name="terms"
            required
            checked={formData.terms}
            onChange={handleChange}
          />
          <label>
            {t('privateForm.terms.label')}{' '}
            <button 
              type="button"
              onClick={() => setShowTerms(true)}
              className="terms-link-button"
            >
              {t('privateForm.terms.link')}
            </button>
            <span className="required">*</span>
          </label>
        </div>
        
        {submitError && (
          <div className="error-message">
            {submitError}
          </div>
        )}

        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting || !reclutamientoId}
        >
          {isSubmitting ? t('privateForm.submitting') : t('privateForm.submit')}
        </button>
      </form>

      <Modal 
        isOpen={showTerms} 
        onClose={() => setShowTerms(false)}
      >
        <PrivateTerms 
          onAccept={handleAcceptTerms}
          onCancel={() => setShowTerms(false)}
        />
      </Modal>
    </>
  );
};

export default PrivateApplicationForm; 